import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useVideos } from "@/components/HeroVideo";
import { LOGO_URL } from "@/lib/api";
import { StripArrow, scrollStrip } from "@/components/StripArrow";

const Tile = ({ v, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(v)}
    data-testid={`portrait-video-tile-${v.id}`}
    className="group relative w-44 sm:w-52 aspect-[9/16] flex-shrink-0 mx-2 snap-center rounded-xl overflow-hidden bg-[#1E3048] border border-[#8FA58A]/30 shadow-lg transition-transform duration-300 hover:scale-[1.04] hover:border-[#E2705A]"
  >
    <img
      src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`}
      alt={v.title || "Video"}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = LOGO_URL;
        e.currentTarget.className = "absolute inset-0 w-full h-full object-contain p-8 opacity-70";
      }}
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#121E2B]/90 via-transparent to-transparent" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="w-14 h-14 rounded-full bg-[#E2705A] text-[#FDFBF7] flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
        <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
      </span>
    </div>
    {v.title && (
      <p className="absolute bottom-3 left-3 right-3 text-left text-xs font-semibold text-[#FDFBF7] line-clamp-2">
        {v.title}
      </p>
    )}
  </button>
);

export const VideoMarquee = () => {
  const { data = [] } = useVideos();
  const [idx, setIdx] = useState(null);
  const trackRef = useRef(null);
  const portraits = data.filter((v) => v.type === "portrait");
  if (!portraits.length) return null;
  const open = idx !== null ? portraits[idx] : null;
  const step = (d) => setIdx((i) => (i + d + portraits.length) % portraits.length);
  return (
    <section className="mt-12 py-6 bg-[#1B2A41] relative" data-testid="portrait-video-marquee">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 mb-4 flex items-baseline justify-between">
        <h2 className="font-type font-bold text-base md:text-lg text-[#E2705A] uppercase tracking-wider">
          Watch &amp; Share
        </h2>
        <span className="text-xs text-[#8FA58A] font-type">{portraits.length} videos · swipe or use the arrows</span>
      </div>
      <div className="relative">
        <StripArrow dir={-1} onClick={() => scrollStrip(trackRef, -1)} testId="video-strip-prev" label="Previous videos" className="left-1 sm:left-3" />
        <StripArrow dir={1} onClick={() => scrollStrip(trackRef, 1)} testId="video-strip-next" label="Next videos" className="right-1 sm:right-3" />
        <div ref={trackRef} data-testid="video-strip" className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth px-11 sm:px-14 py-2 no-scrollbar">
          {portraits.map((v, i) => (
            <Tile key={v.id} v={v} onOpen={() => setIdx(i)} />
          ))}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent
          data-testid="portrait-video-player"
          className="p-0 bg-black border-[#E2705A]/40 max-w-[min(92vw,420px)] overflow-visible [&>button]:z-20"
        >
          <DialogTitle className="sr-only">{open?.title || "Video"}</DialogTitle>
          {open && (
            <>
              <div className="aspect-[9/16] w-full rounded-lg overflow-hidden">
                <iframe
                  key={open.id}
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${open.youtube_id}?autoplay=1&rel=0`}
                  title={open.title || "Video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {portraits.length > 1 && (
                <>
                  <StripArrow dir={-1} onClick={() => step(-1)} testId="video-player-prev" label="Previous video" className="-left-12 hidden sm:flex" />
                  <StripArrow dir={1} onClick={() => step(1)} testId="video-player-next" label="Next video" className="-right-12 hidden sm:flex" />
                  <div className="flex items-center justify-between px-3 py-2 text-xs text-[#FDFBF7]/80 font-type">
                    <button type="button" onClick={() => step(-1)} className="sm:hidden inline-flex items-center gap-1 text-[#E2705A]"><ChevronLeft className="w-4 h-4" /> Prev</button>
                    <span data-testid="video-player-counter">{idx + 1} / {portraits.length}</span>
                    <button type="button" onClick={() => step(1)} className="sm:hidden inline-flex items-center gap-1 text-[#E2705A]">Next <ChevronRight className="w-4 h-4" /></button>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
